import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../index";

const description = `Make authenticated network requests to APIs on behalf of the user. This tool leverages the user's session cookies for authentication.

Use this tool to interact with APIs returned from search  and any other authenticated endpoints that require the user's session.

before calling explain to the user why you are calling the API and what you are doing.

Always use pagination when possible.

IMPORTANT: DO NOT ALTER THE RETURNED URL DO NOT OMIT ANYTHING FROM THE URL. 
Even if the host or source is in the path you must leave it. 
`;
const parameters = z.object({
  method: z
    .enum(["GET", "POST", "PUT", "PATCH", "DELETE"])
    .describe("HTTP method to use"),
  api: z
    .string()
    .describe(
      "The API to use (e.g: zsl-api, tables, interfaces, gulliver, implementation_registry, authentications, zap-guesser, chatbots)",
    ),
  path: z
    .string()
    .describe(
      "The API path as given from the search tool, do not omit anything from the path. ",
    ),
  body: z
    .any()
    .optional()
    .describe("Request body (for POST, PUT, PATCH requests)"),
  headers: z
    .record(z.string())
    .optional()
    .describe("Additional headers to include"),
  requestApprovalMessage: z
    .string()
    .optional()
    .describe(
      "A message to the user describing the request and why it is needed and asking for approval",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Looking at your Zaps...' or 'Checking contacts...'",
    ),
});

export const networkRequestAtom = atom<JotaiTool<z.infer<typeof parameters>>>(
  () => ({
    name: "network_request",
    description: description,
    parameters: parameters,
    execute: atom(null, async (_get, _set, { args }) => {
      try {
        const { method, api, path, body, headers = {} } = args;

        // Always use the generic network request tool proxy
        const proxyUrl = "/api/canvasui/network-request-tool-proxy";
        let finalBody = body;

        // Add host and path parameters based on method
        let finalUrl;
        if (method === "GET") {
          const urlObj = new URL(proxyUrl, window.location.origin);
          urlObj.searchParams.set("api", api);
          urlObj.searchParams.set("path", path);
          finalUrl = urlObj.pathname + urlObj.search;
        } else {
          // For POST/PUT/PATCH, add host and path to the body
          finalBody = {
            ...body,
            host: api,
            path: path,
          };
          finalUrl = proxyUrl;
        }

        const fetchOptions: RequestInit = {
          method,
          headers: {
            "Content-Type": "application/json",
            ...headers,
          },
          credentials: "include", // Include cookies for authentication
        };

        if (finalBody && ["POST", "PUT", "PATCH"].includes(method)) {
          fetchOptions.body = JSON.stringify(finalBody);
        }
        const response = await fetch(finalUrl, fetchOptions);
        const responseData = await response.json().catch(() => null);

        if (!response.ok) {
          // For error responses, provide more detailed information
          return {
            content: [
              {
                type: "text",
                text: `Request failed with status ${response.status}. Error: ${JSON.stringify(responseData, null, 2)}. Host: ${api}, Path: ${path}`,
              },
            ],
          };
        }

        return {
          content: [
            {
              type: "text",
              text: `Request completed with status ${response.status}. Response: ${JSON.stringify(responseData, null, 2)}`,
            },
          ],
        };
      } catch (error) {
        return {
          content: [
            {
              type: "text",
              text: `Network request failed: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          ],
        };
      }
    }),
  }),
);
networkRequestAtom.debugLabel = "networkRequestAtom";
